import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell, Legend,
  PieChart, Pie, RadialBarChart, RadialBar
} from 'recharts';
import { FiTrendingUp, FiActivity, FiTarget, FiAward, FiArrowUpRight, FiArrowDownRight, FiCalendar } from 'react-icons/fi';

const C = { sky: '#7ea52e', purple: '#1a1a18', green: '#7ea52e', orange: '#9ec25a', red: '#1a1a18', cyan: '#b7d083' };

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(126,165,46,0.12)', borderRadius: 14,
      padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    }}>
      <p style={{ color: '#0f172a', fontSize: 12, fontWeight: 700, marginBottom: 6, fontFamily: 'Poppins, sans-serif' }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.stroke }} />
          <span style={{ color: '#475569', fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const SectionHeader = ({ title, subtitle, icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    {icon && <div style={{ width: 32, height: 32, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.sky, fontSize: 15 }}>{icon}</div>}
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 11, color: '#abab9f', margin: '2px 0 0', fontWeight: 500 }}>{subtitle}</p>}
    </div>
  </div>
);

const CustomLegend = ({ payload }) => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 24, paddingTop: 8 }}>
    {payload?.map((e, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: e.color }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{e.value}</span>
      </div>
    ))}
  </div>
);

const PieLegend = ({ data }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 0' }}>
    {data.map((d, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px', borderRadius: 8, background: 'var(--bg-primary)' }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#334155' }}>{d.name}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{d.value?.toLocaleString()}</span>
      </div>
    ))}
  </div>
);

const TIME_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'All Time', days: 365 },
];

export default function CampaignPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(7);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/campaign-performance?days=${range}`)
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [range]);

  const stats = data?.stats || {};
  const trendData = data?.trend || [];
  const campaignComparison = data?.campaignComparison || [];
  const typeBreakdown = data?.typeBreakdown || [];

  /* KPI Cards */
  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0);
  const deliveryRate = totalMessages > 0 ? ((stats.totalSent || 0) / totalMessages * 100).toFixed(1) : 0;
  const avgPerDay = trendData.length > 0
    ? Math.round(trendData.reduce((a, d) => a + (d.campaigns || 0), 0) / trendData.length)
    : 0;

  const kpis = [
    { icon: <FiTrendingUp />, label: 'Total Campaigns', value: (stats.totalCampaigns || 0).toLocaleString(), color: C.sky, bg: '#eef3e6', trend: stats.activeCampaigns > 0 ? `${stats.activeCampaigns} active` : 'None active', up: stats.activeCampaigns > 0 },
    { icon: <FiActivity />, label: 'Messages Sent', value: (stats.totalSent || 0).toLocaleString(), color: C.green, bg: '#eef3e6', trend: `${(stats.todaySent || 0).toLocaleString()} today`, up: (stats.todaySent || 0) > 0 },
    { icon: <FiTarget />, label: 'Delivery Rate', value: `${deliveryRate}%`, color: deliveryRate >= 90 ? C.green : deliveryRate >= 70 ? C.orange : C.red, bg: deliveryRate >= 90 ? '#eef3e6' : deliveryRate >= 70 ? '#e5edda' : '#f0f0ed', trend: `${(stats.totalFailed || 0).toLocaleString()} failed`, up: deliveryRate >= 80 },
    { icon: <FiAward />, label: 'Avg/Day', value: avgPerDay.toLocaleString(), color: C.purple, bg: '#f0f0ed', trend: `${trendData.length} day span`, up: avgPerDay > 0 },
  ];

  /* Donut for type breakdown */
  const TYPE_COLORS = {
    whatsapp: '#7ea52e', whatsapp_image: '#6b8f27', whatsapp_video: '#57721f',
    whatsapp_document: '#455a19', sms: '#7ea52e', email: '#1a1a18',
    rcs: '#b7d083', newsletter: '#5c7a1f', push_notification: '#7e7e71',
    voice_call: '#abab9f', status_messaging: '#161614', custom: '#c9c9c3'
  };
  const TYPE_LABELS = {
    whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
    whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email',
    rcs: 'RCS', newsletter: 'Newsletter', push_notification: 'Push',
    voice_call: 'Voice', status_messaging: 'Status', custom: 'Custom'
  };

  const donutData = typeBreakdown.map(t => ({
    name: TYPE_LABELS[t._id] || t._id, value: t.total, color: TYPE_COLORS[t._id] || '#c9c9c3'
  })).filter(d => d.value > 0);
  if (donutData.length === 0) donutData.push({ name: 'No Data', value: 1, color: '#e6e6e3' });
  const donutTotal = donutData.reduce((a, c) => a + c.value, 0);

  /* Campaign comparison bar */
  const campaignBars = campaignComparison.map((c, i) => ({
    name: c.name?.length > 12 ? c.name.slice(0, 12) + '…' : c.name,
    Sent: c.totalSent || 0, Failed: c.totalFailed || 0,
  }));

  /* Gauge */
  const gaugeVal = parseFloat(deliveryRate) || 0;
  const gaugeData = [{ name: 'Rate', value: gaugeVal, fill: gaugeVal >= 90 ? C.green : gaugeVal >= 70 ? C.orange : C.red }];

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Campaign Performance</h1>
          <p className="page-subtitle">Real-time campaign trends & engagement analytics</p>
        </div>
        <div style={{ position: 'relative' }}>
          <FiCalendar style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#abab9f', fontSize: 14, pointerEvents: 'none' }} />
          <select
            value={range}
            onChange={e => setRange(Number(e.target.value))}
            className="form-select"
            style={{ paddingLeft: 34, minWidth: 160, fontWeight: 600, fontSize: 13, borderRadius: 12 }}
          >
            {TIME_RANGES.map(r => <option key={r.days} value={r.days}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {kpis.map((k, i) => (
          <div key={i} className="card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -12, right: -12, width: 72, height: 72, borderRadius: '50%', background: k.bg, opacity: 0.4 }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: k.bg, color: k.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{k.icon}</div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: k.up ? C.green : C.red }}>
                  {k.up ? <FiArrowUpRight /> : <FiArrowDownRight />} {k.trend}
                </span>
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#abab9f', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{k.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', fontFamily: 'Poppins, sans-serif', lineHeight: 1.1 }}>{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════════════
          MAIN CHART — Campaign Performance Trend
          ══════════════════════════════════════════════ */}
      <div className="chart-container" style={{ marginBottom: 24 }}>
        <SectionHeader
          icon={<FiTrendingUp />}
          title="Campaign Performance Trend"
          subtitle={`Campaigns & engagement over the last ${range} days`}
        />
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={trendData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="perfCampaigns" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7ea52e" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#7ea52e" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="perfEngagement" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1a1a18" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#1a1a18" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke="#f4f4f3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: '#abab9f', fontSize: 11, fontWeight: 500 }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              tick={{ fill: '#abab9f', fontSize: 11 }}
              axisLine={false} tickLine={false}
            />
            <Tooltip content={<GlassTooltip />} />
            <Legend content={<CustomLegend />} />
            <Area
              type="monotone" dataKey="campaigns" name="Campaigns"
              stroke="#7ea52e" strokeWidth={2.5}
              fill="url(#perfCampaigns)"
              dot={false} activeDot={{ r: 5, stroke: '#7ea52e', strokeWidth: 2, fill: '#fff' }}
              animationDuration={1200}
            />
            <Area
              type="monotone" dataKey="engagement" name="Engagement"
              stroke="#1a1a18" strokeWidth={2.5}
              fill="url(#perfEngagement)"
              dot={false} activeDot={{ r: 5, stroke: '#1a1a18', strokeWidth: 2, fill: '#fff' }}
              animationDuration={1400}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Row 2: Campaign Comparison + Type Donut + Gauge ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.5fr', gap: 20, marginBottom: 24 }}>
        {/* Campaign Comparison Bar */}
        <div className="chart-container">
          <SectionHeader title="Top Campaigns" subtitle="Sent vs Failed comparison" />
          {campaignBars.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={campaignBars} barGap={4} barSize={14} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="perfBarG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#9ec25a" /><stop offset="100%" stopColor="#7ea52e" />
                  </linearGradient>
                  <linearGradient id="perfBarR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#333330" /><stop offset="100%" stopColor="#1a1a18" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#f4f4f3" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#abab9f', fontSize: 10, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#abab9f', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Legend content={<CustomLegend />} />
                <Bar dataKey="Sent" fill="url(#perfBarG)" radius={[8, 8, 0, 0]} animationDuration={1000} />
                <Bar dataKey="Failed" fill="url(#perfBarR)" radius={[8, 8, 0, 0]} animationDuration={1200} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No campaign data</h3></div>
          )}
        </div>

        {/* Type Breakdown Donut */}
        <div className="chart-container">
          <SectionHeader title="Channel Split" subtitle="By campaign type" />
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={3} dataKey="value" stroke="none" cornerRadius={4}>
                {donutData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip content={<GlassTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {donutTotal > 1 && (
            <div style={{ textAlign: 'center', marginTop: -20, position: 'relative', zIndex: 2 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', fontFamily: 'Poppins' }}>{donutTotal.toLocaleString()}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Messages</div>
            </div>
          )}
          <PieLegend data={donutData.filter(d => d.name !== 'No Data')} />
        </div>

        {/* Delivery Gauge */}
        <div className="chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <SectionHeader title="Delivery" />
          <ResponsiveContainer width="100%" height={150}>
            <RadialBarChart cx="50%" cy="55%" innerRadius="55%" outerRadius="90%" startAngle={180} endAngle={0} data={gaugeData} barSize={12}>
              <RadialBar background={{ fill: '#f4f4f3' }} dataKey="value" cornerRadius={8} animationDuration={1500} />
              <Tooltip content={<GlassTooltip />} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: -16 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: gaugeData[0].fill, fontFamily: 'Poppins' }}>{deliveryRate}%</div>
            <div style={{ fontSize: 10, color: '#abab9f', fontWeight: 600 }}>SUCCESS</div>
          </div>
        </div>
      </div>

      {/* ── Top Campaigns Table ── */}
      {campaignComparison.length > 0 && (
        <div className="card">
          <SectionHeader title="Campaign Leaderboard" subtitle="Top performing campaigns ranked by delivery" />
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th><th>Campaign</th><th>Type</th><th>Status</th>
                  <th>Sent</th><th>Failed</th><th>Total</th><th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {campaignComparison.map((c, i) => {
                  const total = (c.totalSent || 0) + (c.totalFailed || 0);
                  const rate = total > 0 ? ((c.totalSent || 0) / total * 100).toFixed(1) : 0;
                  return (
                    <tr key={c._id || i}>
                      <td style={{ fontWeight: 700, color: i < 3 ? C.sky : '#abab9f' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{c.name}</td>
                      <td><span className={`badge badge-${c.type}`}>{(c.type || '').replace('_', ' ')}</span></td>
                      <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                      <td style={{ color: C.green, fontWeight: 600 }}>{(c.totalSent || 0).toLocaleString()}</td>
                      <td style={{ color: C.red, fontWeight: 600 }}>{(c.totalFailed || 0).toLocaleString()}</td>
                      <td style={{ fontWeight: 600 }}>{total.toLocaleString()}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#f4f4f3', overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 3, width: `${rate}%`, background: rate >= 90 ? C.green : rate >= 70 ? C.orange : C.red, transition: 'width 1s ease' }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: rate >= 90 ? C.green : rate >= 70 ? C.orange : C.red, minWidth: 42 }}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
